import type { BlogPuckData, LandingPuckData } from "@/puck/types";

type PuckData = LandingPuckData | BlogPuckData;

type PuckBlock = {
  type?: string;
  props?: Record<string, unknown>;
};

function isBlockArray(value: unknown): value is PuckBlock[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item &&
        typeof item === "object" &&
        "type" in item &&
        typeof (item as PuckBlock).type === "string",
    )
  );
}

function findPropInBlocks(
  blocks: PuckBlock[],
  blockId: string,
  propName: string,
): unknown {
  for (const block of blocks) {
    const props = block.props ?? {};
    if (props.id === blockId) {
      return props[propName];
    }

    for (const value of Object.values(props)) {
      if (isBlockArray(value)) {
        const nested = findPropInBlocks(value, blockId, propName);
        if (nested !== undefined) {
          return nested;
        }
      }
    }
  }

  return undefined;
}

function setPropInBlocks(
  blocks: PuckBlock[],
  blockId: string,
  propName: string,
  nextValue: unknown,
): boolean {
  for (const block of blocks) {
    const props = block.props ?? {};
    if (props.id === blockId) {
      props[propName] = nextValue;
      return true;
    }

    for (const value of Object.values(props)) {
      if (isBlockArray(value) && setPropInBlocks(value, blockId, propName, nextValue)) {
        return true;
      }
    }
  }

  return false;
}

/** Find a prop value on a block (including nested slots) by block id. */
export function findPuckBlockProp(
  data: PuckData | null | undefined,
  blockId: string,
  propName: string,
): unknown {
  if (!data || !Array.isArray(data.content)) {
    return undefined;
  }
  return findPropInBlocks(data.content as PuckBlock[], blockId, propName);
}

/** Clone Puck data and set a prop on the block with the given id. */
export function setPuckBlockProp(
  data: PuckData,
  blockId: string,
  propName: string,
  nextValue: unknown,
): PuckData {
  const next = JSON.parse(JSON.stringify(data)) as PuckData;
  if (!Array.isArray(next.content)) {
    return next;
  }
  setPropInBlocks(next.content as PuckBlock[], blockId, propName, nextValue);
  return next;
}
